// src/assets/components/PlaceholderB.jsx
import React, { useState, forwardRef } from "react";
import "../../styles/colorSystem.css";
import info_red from "../icons/warning/info_red.svg";
import cancel from "../icons/System/cancel.svg";
import Tooltip from "./Tooltip";
import deleteIcon from "../icons/System/delete.svg";

const PlaceholderB = forwardRef(function PlaceholderB(
  {
    /** 타이틀 영역 */
    label,
    labelIcon,
    labelTooltip,

    /** 필수 여부 */
    required = false,

    /** 인풋 */
    placeholder = "",
    value,
    onChange,
    maxLength,

    // 🔽 여기 추가
    type = "text",
    inputMode,
    pattern,
    autoComplete = "off",

    /** 사이즈: 기본(md)은 기존과 동일, lg일 때 높이+텍스트 스타일 변경 */
    size = "md", // "md" | "lg"

    /** 인풋 안 아이콘 */
    rightIconMode = "none", // "none" | "static" | "clear"
    rightIcon,

    /** 하단 영역 */
    showCounter = false,
    validation,

    /** 모드: "input" | "display" | "click" */
    mode = "input",

    /** 클릭 전용 모드일 때 외부 onClick */
    onClick,

    /** 외부에서 blur 이벤트 받기 */
    onBlur,
  },
  inputRef
) {
  const [focused, setFocused] = useState(false);
  const [showLabelTooltip, setShowLabelTooltip] = useState(false);

  const safeValue = value ?? "";
  const length = safeValue.length;
  const isEmpty = safeValue === "";

   const isLarge = size === "lg";

  const state =
    !focused && isEmpty
      ? "default"
      : focused && isEmpty
        ? "before"
        : focused && !isEmpty
          ? "while"
          : !focused && !isEmpty
            ? "after"
            : "default";

  const rawValidation = validation ? validation(safeValue) || {} : {};
  const { status = "default", message = "" } = rawValidation;

  const effectiveStatus = focused ? "default" : status;
  const effectiveMessage = focused ? "" : message;

  const hasError = effectiveStatus === "error";
  const hasMessage = !!effectiveMessage;

  const isDisplay = mode === "display";
  const isClick = mode === "click";

  /** 스타일 계산 */
  const borderClass =
    state === "before" || state === "while"
      ? "border-border-primary"
      : "border-border-default";

  const containerBorderClass = isDisplay
    ? "border-border-default"
    : borderClass;

  const containerBgClass = isDisplay
    ? "bg-surface-light-subtle"
    : "bg-surface-light-white";

  const captionClass = hasError
    ? "text-text-danger"
    : "text-text-subtler";

  const counterClass = hasError
    ? "text-text-danger"
    : "text-text-subtler";

  const inputTextClass =
    state === "default" || state === "before"
      ? "text-text-disabled"
      : "text-text-default";

  // size에 따른 높이/정렬/텍스트 스타일
  const containerSizeClass = isLarge ? "h-[120px]" : "h-[48px]";
  const containerAlignClass = isLarge ? "items-start" : "items-center";
  const textSizeClass = isLarge ? "text-c2" : "text-b1"; // 기본은 b1, large는 c2
  const containerPaddingClass = isLarge ? "px-[16px] py-[12px]" : "py-[12px] px-[20px]";

  /** 값 변경 핸들러 (input 모드에서만 사용) */
  const handleChange = (e) => {
    let next = e.target.value;

    if (typeof maxLength === "number") {
      next = next.slice(0, maxLength);
    }

    onChange?.({
      ...e,
      target: { ...e.target, value: next },
    });
  };

  /** clear 모드일 때 값 비우기 */
  const handleClear = () => {
    onChange?.({
      target: { value: "" },
    });
  };

  /** 아이콘 표시 여부 계산 */
  const showStaticIcon =
    rightIconMode === "static" && !!rightIcon;

  // clear 모드일 땐 rightIcon props 상관없이 값이 있을 때만 표시
  const showClearIcon =
    rightIconMode === "clear" && safeValue.length > 0 && state === "while";

  const showTitleArea = !!label || !!labelIcon;

  const showBottomRow =
    hasMessage || (showCounter && typeof maxLength === "number");

  /** 메인 영역 렌더링: 모드별로 분기 */
  const renderMainContent = () => {
    if (isDisplay || isClick) {
      return (
        <div
          className={`
            flex-1 min-w-0 ${textSizeClass} truncate
            ${isEmpty ? "text-text-disabled" : "text-text-subtle"}
          `}
        >
          {isEmpty ? placeholder : safeValue}
        </div>
      );
    }

    // size="lg"일 때는 여러 줄 입력 textarea
    if (isLarge) {
      return (
        <textarea
          ref={inputRef}
          value={safeValue}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={(e) => {
            setFocused(false);
            onBlur?.(e);
          }}
          placeholder={placeholder}
          required={required}
          rows={4}
          className={`
            flex-1 min-w-0 bg-transparent outline-none resize-none
            ${textSizeClass} ${inputTextClass}
            placeholder:text-text-disabled
          `}
        />
      );
    }

    // 입력 가능한 인풋 모드
    return (
      <input
        ref={inputRef}
        value={safeValue}
        onChange={handleChange}        
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
        placeholder={placeholder}
        required={required}
        type={type}               // 🔹 추가
        inputMode={inputMode}     // 🔹 추가
        pattern={pattern}         // 🔹 추가
        autoComplete={autoComplete} // 🔹 추가
        className={`          
          flex-1 min-w-0 bg-transparent outline-none
          ${textSizeClass} ${inputTextClass}
          placeholder:text-text-disabled
        `}
      />
    );
  };

  return (
    <div className="flex flex-col gap-[8px] relative">
      {/* 1. 타이틀 영역 */}
      {showTitleArea && (
        <div className="flex items-center">
          {label && (
            <span className="text-c2 text-text-subtler flex items-center gap-[2px]">
              <span>{label}</span>
              {required && <span className="text-text-danger">*</span>}
            </span>
          )}

          {labelIcon && (
            <button
              type="button"
              onClick={() => setShowLabelTooltip((prev) => !prev)}
              className={`relative flex items-center justify-center ${label ? "ml-[4px]" : ""}`}
            >
              {labelIcon}

              {/* 툴팁: 공통 Tooltip 컴포넌트 사용 */}
              {labelTooltip && showLabelTooltip && (
                <Tooltip
                  text={labelTooltip}
                  selectable={false} // 예전처럼 아이콘으로만 열고/닫게
                  positionClass="absolute left-1/2 -translate-x-1/2 bottom-[calc(100%+4px)]"
                />
              )}
            </button>
          )}
        </div>
      )}

      {/* 2. 박스 영역 (input / display / click 공용) */}
      <div
        className={`
          flex ${containerAlignClass} w-full ${containerSizeClass}
          rounded-[12px] border ${containerPaddingClass}
          ${containerBgClass} ${containerBorderClass}
          ${isClick ? "cursor-pointer" : ""}
        `}
        onClick={
          isClick && onClick
            ? (e) => {
              e.preventDefault();
              e.stopPropagation();
              onClick(e);
            }
            : undefined
        }
        role={isClick ? "button" : undefined}
      >
        {renderMainContent()}

        {/* 항상 보이는 아이콘 (static) */}
        {showStaticIcon && (
          <div className="ml-[4px] w-[24px] h-[24px] flex items-center justify-center">
            {rightIcon}
          </div>
        )}

        {/* clear 모드: 값 있을 때만, 무조건 cancel 아이콘 사용 */}
        {showClearIcon && (
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleClear();
            }}
            className="ml-[4px] w-[24px] h-[24px] flex items-center justify-center shrink-0"
          >
            <img src={cancel} alt="삭제" />
          </button>
        )}
      </div>

      {/* 하단 캡션 / 카운터 */}
      {showBottomRow && (
        <div className="flex items-center justify-between">
          {/* 왼쪽: lg일 때는 전체 삭제, 그 외에는 메시지 */}
          <div className={`flex items-center gap-[4px] text-c2 ${captionClass}`}>
            {isLarge && length > 0 ? (
              <button
                type="button"
                onClick={handleClear}
                className="flex items-center gap-[4px] text-text-danger"
              >
                <img src={deleteIcon} alt="전체 삭제" className="w-[20px] h-[20px]" />
                <span>전체 삭제</span>
              </button>
            ) : (
              <>
                {hasMessage && hasError && (
                  <img src={info_red} alt="info_red" />
                )}
                {hasMessage && effectiveMessage}
              </>
            )}
          </div>

          {/* 오른쪽: 카운터 */}
          {showCounter && typeof maxLength === "number" && (
            <div className={`text-c2 ${counterClass}`}>
              {length} / {maxLength}자 이내
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default PlaceholderB;
